import { Features } from '~/components/FeatureToggle';
import type { IsroResult } from '~/state/atoms/isroResultAtoms';
import AppConfig from '~/utils/appconfig';

const ISRO_MODEL_TOKEN = 'isro';

export const ISRO_DEFAULT_SAMPLES = 5;
export const ISRO_MIN_SAMPLES = 1;
export const ISRO_MAX_SAMPLES = 10;

function containsIsroToken(value: string | undefined): boolean {
    return (value ?? '').toLowerCase().includes(ISRO_MODEL_TOKEN);
}

export type IsroModelIdentity = {
    name?: string;
    label?: string;
};

export function isIsroModel<T extends IsroModelIdentity>(
    model: T | undefined
): model is T {
    if (!model) {
        return false;
    }
    return containsIsroToken(model.name) || containsIsroToken(model.label);
}

// Never in a clinical build, whatever the flag says.
export function isroDeploymentEnabled(): boolean {
    return (
        AppConfig.isFeatureEnabled(Features.ISRO_HN) && !AppConfig.clinicalUse
    );
}

export function isValidIsroSampleCount(count: number): boolean {
    return (
        Number.isInteger(count) &&
        count >= ISRO_MIN_SAMPLES &&
        count <= ISRO_MAX_SAMPLES
    );
}

export type IsroSampleSeries = {
    seriesUid: string;
    seriesMetadata: { seriesDescription: string };
};

export function sampleIndexFromDescription(
    seriesDescription: string
): number | undefined {
    const match = /\bsample\s+(\d+)\b/i.exec(seriesDescription ?? '');
    if (!match) {
        return undefined;
    }
    const index = Number.parseInt(match[1], 10);
    return Number.isInteger(index) && index > 0 ? index : undefined;
}

export function hasGenerationOrder(
    images: readonly IsroSampleSeries[]
): boolean {
    const indices = images.map((image) =>
        sampleIndexFromDescription(image.seriesMetadata.seriesDescription)
    );
    return (
        indices.length > 0 &&
        indices.every((index) => index !== undefined) &&
        new Set(indices).size === indices.length
    );
}

export function selectIsroSampleSeries<T extends IsroSampleSeries>(
    images: readonly T[],
    model: IsroModelIdentity
): T[] {
    // The series description carries the label when the catalog has one, else the name.
    const identifiers = [model.name, model.label]
        .map((identifier) => (identifier ?? '').trim().toLowerCase())
        .filter((identifier) => identifier.length > 0);

    if (identifiers.length === 0) {
        // An empty identifier would match every series, including the patient's own scans.
        return [];
    }

    const matches = images.filter((image) => {
        const description = (
            image.seriesMetadata.seriesDescription ?? ''
        ).toLowerCase();
        return identifiers.some((identifier) =>
            description.includes(identifier)
        );
    });

    const indices = matches.map((image) =>
        sampleIndexFromDescription(image.seriesMetadata.seriesDescription)
    );
    const allIndexed =
        matches.length > 0 && indices.every((index) => index !== undefined);
    if (allIndexed && new Set(indices).size === indices.length) {
        return matches
            .map((image, position) => ({ image, index: indices[position]! }))
            .sort((a, b) => a.index - b.index)
            .map((entry) => entry.image);
    }

    return [...matches].sort((a, b) => a.seriesUid.localeCompare(b.seriesUid));
}

export type IndexedSampleField<T> = {
    readonly index: number | undefined;
    readonly field: T | undefined;
};

export function pairFieldsToSamples<T>(
    samples: readonly IsroSampleSeries[],
    fields: readonly IndexedSampleField<T>[]
): (T | undefined)[] {
    const none = samples.map(() => undefined);
    if (!hasGenerationOrder(samples)) {
        return none;
    }

    const byIndex = new Map<number, T>();
    for (const { index, field } of fields) {
        if (index === undefined || field === undefined) {
            continue;
        }
        if (byIndex.has(index)) {
            return none;
        }
        byIndex.set(index, field);
    }

    const paired = samples.map((sample) => {
        const index = sampleIndexFromDescription(
            sample.seriesMetadata.seriesDescription
        );
        return index === undefined ? undefined : byIndex.get(index);
    });
    return paired.some((field) => field === undefined) ? none : paired;
}

export interface IsroResultInput<T extends IsroSampleSeries> {
    readonly jobId: string;
    readonly model: IsroModelIdentity;
    readonly referenceSeriesUid: string;
    readonly images: readonly T[];
    readonly registrations: readonly {
        readonly sopInstanceUid: string;
        readonly seriesDescription: string;
    }[];
}

export function isroResultFrom<T extends IsroSampleSeries>(
    input: IsroResultInput<T>
): IsroResult | null {
    const samples = selectIsroSampleSeries(input.images, input.model);
    if (samples.length === 0) {
        return null;
    }
    const registrationUids = pairFieldsToSamples(
        samples,
        input.registrations.map((registration) => ({
            index: sampleIndexFromDescription(registration.seriesDescription),
            field: registration.sopInstanceUid,
        }))
    );
    return {
        jobId: input.jobId,
        modelName: input.model.label || input.model.name || '',
        referenceSeriesUid: input.referenceSeriesUid,
        samples: samples.map((image, position) => ({
            number:
                sampleIndexFromDescription(
                    image.seriesMetadata.seriesDescription
                ) ?? position + 1,
            seriesUid: image.seriesUid,
            registrationSopInstanceUid: registrationUids[position],
        })),
    };
}
